import { PRACTICE_BLANK_UUID } from "common/globals";
import {
  CellAttributes,
  isCellNotationType,
  NotationAttributes,
  NotationCreationAttributes,
} from "common/baseTypes";
import { BoardType, NotationType, NotationTypeValues } from "common/unions";
import {
  ensureNumberedParts,
  hasPracticeSections,
} from "common/practiceParts";
import type { PracticeLocale } from "common/practiceLocale";
import {
  formatPracticeProblemPrompt,
  getPracticeQuestionTemplateByUUId,
  practiceDiagramStems,
  practiceTemplateStemParts,
} from "common/practiceQuestionTemplates";
import { localizePracticeTemplate } from "common/practiceTemplateLocale";
import { usePracticeLocaleStore } from "../store/pinia/practiceLocaleStore";
import useApiHelper from "./apiHelper";
import { useNotationStore } from "../store/pinia/notationStore";
import { usePracticeStore } from "../store/pinia/practiceStore";
import userOutgoingOperations from "./userOutgoingOperationsHelper";
import { practiceGeneratedFigureOverlay, practiceTemplateDiagramOverlay } from "./practiceStemOverlayHelper";
import type { LessonTemplateNotation } from "common/lessonTemplates";
import { practiceGeneratedFigure } from "common/practiceGenerators";

export { PRACTICE_BLANK_UUID };

/** Marks a local PRACTICE notation as the pasted/uploaded problem on a blank sheet. */
export const PRACTICE_PROBLEM_ROLE = "problem" as const;

export type PracticeNotationExtras = {
  practiceRole?: typeof PRACTICE_PROBLEM_ROLE;
};

export function isPracticeProblemNotation(
  notation: NotationAttributes | null | undefined,
): boolean {
  return (
    (notation as (NotationAttributes & PracticeNotationExtras) | null)
      ?.practiceRole === PRACTICE_PROBLEM_ROLE
  );
}

/**
 * Practice / AI-tutor board boundary.
 * Owns local persistence, load of stem + practice layer, selection rules, and canEdit.
 * Lesson/answer paths must not grow practice branches — call this adapter instead.
 */
export function isPracticeBoard(): boolean {
  return useNotationStore().getParent()?.type === "PRACTICE";
}

export function isBlankPracticeBoard(): boolean {
  return (
    isPracticeBoard() &&
    useNotationStore().getParent()?.uuid === PRACTICE_BLANK_UUID
  );
}

/** Unsubmitted blank (or catalog before stem load): paste goes to the problem pane. */
export function shouldCapturePracticeProblemPaste(): boolean {
  if (!isPracticeBoard()) return false;
  try {
    return !usePracticeStore().getSession(getPracticeQuestionUUId()).submitted;
  } catch {
    return false;
  }
}

export function getPracticeQuestionUUId(): string {
  const parent = useNotationStore().getParent();
  if (!parent) {
    throw new Error("practice board parent is not set");
  }
  return parent.uuid;
}

/** Practice boards are always editable for the active learner. */
export function canEdit(): boolean {
  return isPracticeBoard();
}

export function isPracticeLayer(
  notation: NotationAttributes | null | undefined,
): boolean {
  return notation?.boardType === "PRACTICE";
}

export function createLocalPracticeNotation(
  notation: NotationCreationAttributes & PracticeNotationExtras,
): NotationAttributes & PracticeNotationExtras {
  const questionUUId = getPracticeQuestionUUId();
  const uuid = crypto.randomUUID();
  const newNotation = {
    ...notation,
    uuid,
    boardType: "PRACTICE" as BoardType,
    parentUUId: questionUUId,
  } as NotationAttributes & PracticeNotationExtras;

  usePracticeStore().upsertNotation(questionUUId, newNotation);
  userOutgoingOperations().syncOutgoingPracticeNotation("create", newNotation);
  return newNotation;
}

export function persistPracticeNotation(notation: NotationAttributes) {
  if (!isPracticeBoard()) return;
  const questionUUId = getPracticeQuestionUUId();
  const stored = {
    ...notation,
    boardType: "PRACTICE" as BoardType,
    parentUUId: questionUUId,
  };
  usePracticeStore().upsertNotation(questionUUId, stored);
  userOutgoingOperations().syncOutgoingPracticeNotation("update", stored);
}

/** Lock current blank-sheet work as the question stem, then the student solves. */
export function markCurrentPracticeNotationsAsProblem() {
  if (!isPracticeBoard()) return;
  const notationStore = useNotationStore();
  const snapshot = [...notationStore.getNotations()];
  notationStore.beginUndoGroup();
  try {
    for (const notation of snapshot) {
      if (notation.boardType !== "PRACTICE") continue;
      if (isPracticeProblemNotation(notation)) continue;
      const marked = {
        ...notation,
        practiceRole: PRACTICE_PROBLEM_ROLE,
      } as NotationAttributes & PracticeNotationExtras;
      notationStore.addNotation(marked, true, false);
      persistPracticeNotation(marked);
    }
  } finally {
    notationStore.endUndoGroup();
  }
}

export function removeLocalPracticeNotation(notationUUId: string) {
  if (!isPracticeBoard()) return;
  usePracticeStore().removeNotation(getPracticeQuestionUUId(), notationUUId);
  userOutgoingOperations().syncOutgoingPracticeNotation("remove", notationUUId);
}

function joinStemNotations(notations: NotationAttributes[]): string {
  const lines: string[] = [];
  for (const n of notations) {
    if (n.notationType === "TEXT" || n.notationType === "ANNOTATION") {
      const value = String(
        (n as NotationAttributes & { value?: string }).value ?? "",
      ).trim();
      if (value) lines.push(value);
    }
  }
  if (lines.length) return lines.join("\n");
  const symbols = notations
    .filter(
      (n) =>
        n.notationType === "SYMBOL" ||
        n.notationType === "EXPONENT" ||
        n.notationType === "LOGBASE",
    )
    .map((n) =>
      String((n as NotationAttributes & { value?: string }).value ?? "").trim(),
    )
    .filter(Boolean);
  return symbols.join("");
}

function partsMatch(
  a: { id: string; text: string }[],
  b: { id: string; text: string }[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every((part, i) => part.id === b[i].id && part.text === b[i].text);
}

function currentPracticeLocale(): PracticeLocale {
  return usePracticeLocaleStore().tutorLocale;
}

const generatedQuestionIds = new Set<string>();
const generatedFigures = new Map<string, LessonTemplateNotation[]>();

/** Stem for a minted instance. Checked against the answer stored on the server. */
export function applyGeneratedStem(
  questionUUId: string,
  prompt: string,
  figure: LessonTemplateNotation[] = [],
) {
  const text = prompt.trim();
  if (!questionUUId || !text) return;
  generatedQuestionIds.add(questionUUId);
  generatedFigures.set(questionUUId, figure);
  submitStemIfNeeded(questionUUId, text, [{ id: "1", text }]);
}

export function figureForGeneratedInstance(
  generatorId: string,
  seed: number,
  locale: PracticeLocale,
): LessonTemplateNotation[] {
  return practiceGeneratedFigure(generatorId, seed, locale);
}

/** Catalog stem from the local template so the paste-problem UI never flashes. */
export function applyCatalogStem(
  questionUUId: string,
  locale: PracticeLocale = currentPracticeLocale(),
) {
  if (!questionUUId || questionUUId === PRACTICE_BLANK_UUID) return;
  const template = getPracticeQuestionTemplateByUUId(questionUUId);
  if (!template) return;
  const localized = localizePracticeTemplate(template, locale);
  submitStemIfNeeded(
    questionUUId,
    formatPracticeProblemPrompt(localized, locale),
    practiceTemplateStemParts(localized) ?? undefined,
  );
}

function submitStemIfNeeded(
  questionUUId: string,
  stemText: string,
  parts?: { id: string; text: string }[],
) {
  const practiceStore = usePracticeStore();
  const text = stemText.trim();
  if (!text) return;
  const session = practiceStore.getSession(questionUUId);
  const resolved = parts ?? ensureNumberedParts(text);
  const leftoverNumbers =
    !hasPracticeSections(resolved) &&
    Object.keys(session.partLabelRows ?? {}).length > 0;
  if (
    session.submitted &&
    session.problemText === text &&
    partsMatch(session.parts, resolved) &&
    !leftoverNumbers
  ) {
    return;
  }
  practiceStore.submitProblem(questionUUId, {
    problemText: text,
    parts: resolved,
  });
}

/** Catalog figure from the bundled template (same source the seeder copies into the DB). */
export function catalogDiagramOverlay(
  questionUUId: string,
  locale: PracticeLocale = currentPracticeLocale(),
): NotationAttributes[] {
  const template = getPracticeQuestionTemplateByUUId(questionUUId);
  if (!template) return [];
  return practiceTemplateDiagramOverlay(
    localizePracticeTemplate(template, locale),
    questionUUId,
  );
}

async function fetchStemNotations(
  apiHelper: ReturnType<typeof useApiHelper>,
  questionUUId: string,
): Promise<NotationAttributes[]> {
  const stemNotations: NotationAttributes[] = [];
  for (let i = 0; i < NotationTypeValues.length; i++) {
    const notationType = NotationTypeValues[i] as NotationType;
    try {
      const fetched = await loadStemNotationsByType(
        apiHelper,
        notationType,
        questionUUId,
      );
      if (!fetched) continue;
      fetched.forEach((n) => {
        stemNotations.push({
          ...n,
          notationType,
          boardType: "QUESTION",
        });
      });
    } catch (error) {
      console.warn(
        `Skipping QUESTION ${notationType} notations for ${questionUUId}:`,
        error,
      );
    }
  }
  return stemNotations;
}

/**
 * Load PRACTICE student layer from sessionStorage.
 * Catalog questions: the figure is drawn from the bundled template (no server
 * round trip, so it is there offline, in test, and before seeding); stem text
 * goes in the problem pane. Other questions: QUESTION diagrams come from the API.
 * Blank sheet: local PRACTICE layer only; leftover `practiceRole` stems migrate to the pane.
 */
export async function loadPracticeBoard(questionUUId: string) {
  const notationStore = useNotationStore();
  const apiHelper = useApiHelper();
  const practiceStore = usePracticeStore();

  try {
    notationStore.haltSaveState();

    let diagramOverlay: NotationAttributes[] = [];
    if (questionUUId !== PRACTICE_BLANK_UUID) {
      const template = getPracticeQuestionTemplateByUUId(questionUUId);
      if (template) {
        diagramOverlay = catalogDiagramOverlay(questionUUId);
        applyCatalogStem(questionUUId);
      } else if (generatedQuestionIds.has(questionUUId)) {
        diagramOverlay = practiceGeneratedFigureOverlay(
          generatedFigures.get(questionUUId) ?? [],
          questionUUId,
        );
      } else {
        const stemNotations = await fetchStemNotations(apiHelper, questionUUId);
        if (generatedQuestionIds.has(questionUUId)) {
          diagramOverlay = practiceGeneratedFigureOverlay(
            generatedFigures.get(questionUUId) ?? [],
            questionUUId,
          );
        } else {
          diagramOverlay = practiceDiagramStems(
            stemNotations as (NotationAttributes & { value?: string })[],
          );
          const stemText = joinStemNotations(stemNotations);
          if (stemText) {
            submitStemIfNeeded(questionUUId, stemText);
          } else if (!practiceStore.getSession(questionUUId).problemText?.trim()) {
            submitStemIfNeeded(questionUUId, "Practice question");
          }
        }
      }
    }

    let practiceNotations = practiceStore
      .getNotations(questionUUId)
      .map((n) => ({ ...n, boardType: "PRACTICE" as BoardType }));

    if (questionUUId === PRACTICE_BLANK_UUID) {
      const problems = practiceNotations.filter(isPracticeProblemNotation);
      const work = practiceNotations.filter((n) => !isPracticeProblemNotation(n));
      if (problems.length && !practiceStore.getSession(questionUUId).submitted) {
        const stemText = joinStemNotations(problems);
        if (stemText) submitStemIfNeeded(questionUUId, stemText);
      }
      practiceNotations = work;
    }

    notationStore.setNotations([...diagramOverlay, ...practiceNotations]);

    const session = practiceStore.getSession(questionUUId);
    if (session.submitted && !session.activePartId) {
      practiceStore.setActivePart(
        questionUUId,
        session.parts[0]?.id ?? "1",
      );
    }
  } finally {
    notationStore.activateSaveState();
  }
}

async function loadStemNotationsByType(
  apiHelper: ReturnType<typeof useApiHelper>,
  notationType: NotationType,
  parentUUId: string,
): Promise<NotationAttributes[]> {
  switch (notationType) {
    case "EXPONENT":
    case "LOGBASE":
    case "ANNOTATION":
    case "SYMBOL":
    case "SQRT":
    case "DIVISIONLINE":
    case "LINE":
    case "IMAGE":
    case "TEXT":
    case "CURVE":
    case "CIRCLE":
    case "CONIC":
    case "FREESKETCH":
      return await apiHelper.getNotations(notationType, "QUESTION", parentUUId);
    case "POLYGON":
    case "SQRTSYMBOL":
      return [];
    default:
      throw new Error(`${notationType} :notation type is invalid`);
  }
}

export type PracticeClickHandlers = {
  selectNotation: (uuid: string) => void;
  setSelectedCell: (cell: CellAttributes, show: boolean) => void;
  resetSelectedNotations: () => void;
};

/**
 * Practice selection: QUESTION stem is display-only; PRACTICE layer is editable.
 * Returns true when the click was handled (caller should return).
 * Returns false when there was no DOM hit so the caller can proximity-select
 * thin shapes (lines, curves, etc.).
 */
export function handlePracticeClick(
  clickedNotation: NotationAttributes | null,
  clickedCell: CellAttributes,
  handlers: PracticeClickHandlers,
): boolean {
  if (!isPracticeBoard()) return false;

  if (isPracticeLayer(clickedNotation)) {
    handlers.resetSelectedNotations();
    handlers.selectNotation(clickedNotation!.uuid);
    if (isCellNotationType(clickedNotation!.notationType)) {
      handlers.setSelectedCell(clickedCell, true);
    }
    return true;
  }

  // Missed the SVG element — let caller use distance-based selection.
  if (!clickedNotation) {
    return false;
  }

  // QUESTION stem (or other non-practice): select cell only.
  handlers.resetSelectedNotations();
  handlers.setSelectedCell(clickedCell, true);
  return true;
}
